import React, { useMemo } from 'react';
import { AccordionItem, Box, Preloader,RadioButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { OrganizationTypes } from '@/common/constants';
import BoldSubstring from '@/modules/CarrierConfig/components/CreateCarrierConfig/CreateCarrierConfigForm/OrgLevelSelector/components/BoldSubstring';
import useGetOrganizationQuery, { Organization } from '@/modules/core/queries/useGetOrganization.query';

import { SelectedOrganization } from '../../types';

export const StyledItem = styled(Box)`
  &:hover {
    background-color: ${({ theme }) => theme.colors.accentContainer};
    span {
      color: ${({ theme }) => theme.colors.textAccent};
    }
  }
`;

interface SingleNestedSelectorLevelProps {
  orgLevels: OrganizationTypes[];
  parentId?: string;
  selectedOrgId: string;
  onSelect: (org: SelectedOrganization) => void;
  isSearchUsed: boolean;
  searchValue: string;
  isShown: boolean;
  isLoading?: boolean;
  searchedOrgs?: Organization[];
  currentHightLevel?: OrganizationTypes;
}

export const SingleNestedSelectorLevel: React.FC<SingleNestedSelectorLevelProps> = ({
  orgLevels,
  parentId,
  onSelect,
  selectedOrgId,
  searchedOrgs,
  isSearchUsed,
  searchValue,
  isShown,
  isLoading,
  currentHightLevel,
}) => {
  const currentLevelLabel = useMemo(() => {
    switch (orgLevels[0]) {
      case OrganizationTypes.company:
        return 'Employers';
      case OrganizationTypes.distributor:
        return 'Distributors';
      case OrganizationTypes.partner:
        return 'Partners';
      case OrganizationTypes.subsidiary:
        return 'Subsidiaries';
      default:
        return '';
    }
  }, [orgLevels]);

  const data = useGetOrganizationQuery({
    type: orgLevels[0],
    ...(isSearchUsed ? { searchValue } : {}),
    enabled: !!isShown && !isLoading && !searchedOrgs,
    parentId,
  });

  const preparedData = useMemo(() => {
    if (searchedOrgs && searchedOrgs.length) return searchedOrgs;
    return data.organizations || [];
  }, [searchedOrgs, data.organizations]);

  const isLastItem = useMemo(() => orgLevels.length === 1, [orgLevels]);
  const isHighLevel = useMemo(() => currentHightLevel && orgLevels[0] === currentHightLevel, [orgLevels, currentHightLevel]);

  return (
    <Box height={{ min: 'fit-content' }}>
      <Box margin={{ left: isHighLevel ? 'spacing2' : '34px', top: '9px' }}>
        <Text color="textSecondary" size="12px" style={{ display: 'flex', alignContent: 'center' }}>
          {currentLevelLabel}: ({isHighLevel ? 1 : preparedData.length})
        </Text>
      </Box>

      {data.isLoading ? (
        <Box justify="center" align="center" pad="spacing12">
          <Preloader size={20} />
        </Box>
      ) : isLastItem ? (
        <>
          {preparedData.map((item) => (
            <StyledItem
              key={item.id}
              onClick={(e) => {
                e.stopPropagation();
                onSelect({ id: item.id, name: item.name, type: orgLevels[0] });
              }}
              pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
              margin={{ left: '54px', top: '9px' }}
              round="dropdownItemRound"
              direction="row"
              width="100%"
            >
              <RadioButton
                id={`radio-node-${item.id}`}
                name="single-org-selector-radio"
                checked={selectedOrgId === item.id}
                onChange={() => onSelect({ id: item.id, name: item.name, type: orgLevels[0] })}
                label={
                  <BoldSubstring
                    text={item.name}
                    substring={searchValue}
                    title={item.name}
                  />
                }
              />
            </StyledItem>
          ))}
        </>
      ) : (
        <>
          {preparedData.map((item) => (
            <Box
              key={item.id}
              pad={{ left: isHighLevel ? undefined : 'spacing32' }}
              margin={{ top: 'spacing8' }}
              height={{ min: '24px'}}
              width="100%"
            >
              <AccordionItem
                testId={`${orgLevels[0]}-level-${item.id}`}
                isOptionViewArrow
                text={
                  <StyledItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({ id: item.id, name: item.name, type: orgLevels[0] });
                    }}
                    pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                    margin={{ left: 'spacing2' }}
                    round="dropdownItemRound"
                    direction="row"
                    width="100%"
                  >
                    <RadioButton
                      id={`radio-node-${item.id}`}
                      name="single-org-selector-radio"
                      checked={selectedOrgId === item.id}
                      onChange={() => onSelect({ id: item.id, name: item.name, type: orgLevels[0] })}
                      label={
                        <BoldSubstring
                          text={item.name}
                          substring={searchValue}
                          title={item.name}
                        />
                      }
                    />
                  </StyledItem>
                }
                isLeftArrow
                isExpandedDefault={isHighLevel}
              >
                <SingleNestedSelectorLevel
                  orgLevels={orgLevels.slice(1)}
                  parentId={item.id}
                  selectedOrgId={selectedOrgId}
                  onSelect={onSelect}
                  isSearchUsed={isSearchUsed}
                  searchValue={searchValue}
                  isShown={isShown}
                  isLoading={isLoading}
                  searchedOrgs={item.children}
                />
              </AccordionItem>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default SingleNestedSelectorLevel;
